import { parseRecipeSpec } from './recipe_spec';
import { parseWorkoutSpec } from './workout_spec';
import { parseExperienceSpec } from './experience_spec';

const capitalize = value => value.charAt(0).toUpperCase() + value.slice(1);
const field = (label, value) => value ? `${label}: ${value}` : '';
const section = (title, lines) => lines.length ? [title, ...lines].join('\n') : '';
const join = parts => parts.filter(Boolean).join('\n\n');

export function formatExerciseSpec(exercise) {
  if (exercise.sets && exercise.reps) return `${exercise.sets} × ${exercise.reps}`;
  if (exercise.reps) return exercise.reps;
  if (exercise.sets) return `${exercise.sets} sets`;
  return '';
}

function recipeText(spec) {
  return join([
    spec.title, spec.description, [spec.cuisine, ...spec.tags].filter(Boolean).join(' · '),
    [field('Total time', spec.totalTime), field('Serves', spec.servings), field('Level', capitalize(spec.difficulty)), field('Prep', spec.prepTime), field('Cook', spec.cookTime)].filter(Boolean).join('\n'),
    section('Ingredients', spec.ingredientGroups.flatMap(group => [group.section, ...group.items.map(item => `- ${item}`)].filter(Boolean))),
    section('Instructions', spec.steps.map((step, index) => `${index + 1}. ${step}`)),
    section("Chef's Tips", spec.tips.map(tip => `- ${tip}`)),
  ]);
}

function workoutText(spec) {
  let number = 0;
  return join([
    spec.title, spec.description, spec.equipment.join(' · '),
    [field('Duration', spec.duration), field('Level', capitalize(spec.difficulty)), field('Exercises', spec.exerciseCount), field('Rounds', spec.rounds), field('Burn', spec.calories)].filter(Boolean).join('\n'),
    section('Exercises', spec.exerciseGroups.flatMap(group => [group.section, ...group.items.map(exercise => [
      `${++number}. ${exercise.name}`,
      [formatExerciseSpec(exercise), exercise.rest && `Rest ${exercise.rest}`].filter(Boolean).join(' · '),
      exercise.notes,
    ].filter(Boolean).join('\n'))].filter(Boolean))),
    section('Trainer Tips', spec.tips.map(tip => `- ${tip}`)),
  ]);
}

function planItem(item) {
  return join([
    [`- ${item.title}`, item.detail, item.time, item.location, item.travel, field('Owner', item.owner), item.url].filter(Boolean).join('\n'),
    item.recipe && recipeText(item.recipe),
  ]);
}

function experienceText(spec) {
  return join([
    spec.title, spec.description,
    spec.summary.map(item => field(item.label, item.value)).join('\n'),
    section('What you’ll need', spec.equipment.map(item => `- ${item}`)),
    ...spec.sections.map(group => section(group.title, group.items.map(planItem))),
    ...spec.options.map(option => join([
      option.title, option.detail,
      spec.criteria.map((criterion, index) => field(criterion, option.values[index])).join('\n'),
      section('Strengths', option.pros.map(value => `- ${value}`)),
      section('Tradeoffs', option.cons.map(value => `- ${value}`)), option.url,
    ])),
    spec.recommendation && join([
      field('Best fit', spec.options.find(option => option.id === spec.recommendation.optionId).title),
      section('Why this fits', [spec.recommendation.reason]),
    ]),
    section('Keep in mind', spec.tips.map(tip => `- ${tip}`)),
    spec.supplies.length && join([spec.kind === 'mealplan' ? 'Shopping list' : 'Supplies', ...spec.supplies.map(group => section(group.title, group.items.map(planItem)))]),
    // Copy the practice questions without revealing answers hidden by the card.
    section('Practice', spec.quiz.map((question, index) => [
      `${index + 1}. ${question.question}`, ...question.choices.map(choice => `- ${choice}`),
    ].join('\n'))),
  ]);
}

// Use the same validation and normalized fields as the rendered cards. Invalid
// structures and ordinary JSON/code blocks retain their original copy behavior.
export function guideCardToPlainText(kind, raw) {
  kind = typeof kind === 'string' ? kind.trim().toLowerCase() : '';
  if (kind === 'recipe') {
    const spec = parseRecipeSpec(raw);
    return spec ? recipeText(spec) : null;
  }
  if (kind === 'workout') {
    const spec = parseWorkoutSpec(raw);
    return spec ? workoutText(spec) : null;
  }
  const spec = parseExperienceSpec(raw, kind);
  return spec ? experienceText(spec) : null;
}
